import { lazy, Suspense } from "react";
import Archive from "lucide-react/dist/esm/icons/archive.mjs";
import ArrowLeft from "lucide-react/dist/esm/icons/arrow-left.mjs";
import BookOpen from "lucide-react/dist/esm/icons/book-open.mjs";
import RotateCcw from "lucide-react/dist/esm/icons/rotate-ccw.mjs";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check.mjs";
import type { InboxBrief, InboxBriefSource, ReaderPreferences } from "../types";

const MarkdownArticle = lazy(() => import("./MarkdownArticle"));

export function InboxBriefPane({
  brief,
  loading,
  error,
  online,
  preferences,
  busySource,
  archiveBusy,
  onBack,
  onOpenSource,
  onKeep,
  onRestore,
  onArchive,
  onRetry,
}: {
  brief?: InboxBrief;
  loading: boolean;
  error?: string;
  online: boolean;
  preferences: ReaderPreferences;
  busySource: string;
  archiveBusy: boolean;
  onBack: () => void;
  onOpenSource: (source: InboxBriefSource) => void;
  onKeep: (source: InboxBriefSource) => void;
  onRestore: (source: InboxBriefSource) => void;
  onArchive: () => void;
  onRetry?: () => void;
}) {
  const classes = ["reader-pane", `reader-pane--size-${preferences.typeSize}`, `reader-pane--width-${preferences.width}`, `reader-pane--theme-${preferences.theme}`].join(" ");
  return (
    <section className={classes} aria-label={brief?.title || "Inbox Brief"}>
      <header className="reader-toolbar">
        <button type="button" onClick={onBack} className="reader-toolbar__back"><ArrowLeft aria-hidden="true" />Back to {brief?.status === "archive" ? "Archive" : "Inbox"}</button>
        <div className="reader-toolbar__actions"><span className="brief-trust"><ShieldCheck aria-hidden="true" />Source checked</span>{brief?.status === "inbox" ? <button type="button" disabled={!online || archiveBusy} onClick={onArchive}><Archive aria-hidden="true" /><span>{archiveBusy ? "Archiving…" : "Archive covered"}</span></button> : null}</div>
      </header>
      <div className="reader-scroll">
        {loading && !brief ? <p className="reader-loading" role="status">Opening Inbox Brief…</p> : null}
        {error ? <div className="reader-state inline-recovery" role="alert"><p>{error}</p>{onRetry ? <button type="button" onClick={onRetry}>Retry</button> : null}</div> : null}
        {brief ? (
          <article className="article-sheet inbox-brief-sheet">
            <header className="article-header"><p>INBOX BRIEF</p><h1 tabIndex={-1} data-reader-route-heading>{brief.title}</h1><div>{brief.subtitle}</div></header>
            <Suspense fallback={<p className="reader-loading">Typesetting brief…</p>}>
              <MarkdownArticle markdown={brief.markdown || "This brief is not cached on this device yet."} />
            </Suspense>
            <section className="brief-source-ledger" aria-labelledby="brief-source-heading">
              <h2 id="brief-source-heading">Source ledger</h2>
              <p>Every covered source remains available. Keep an item before archiving the rest.</p>
              {(brief.sources || []).map((source) => (
                <article key={source.item_id} className="brief-source-row">
                  <div><strong>{source.title}</strong><span>{source.site_name}{source.disposition !== "covered" ? ` · ${dispositionLabel(source.disposition)}` : ""}</span><p>{source.takeaway}</p></div>
                  <div className="brief-source-actions">
                    <button type="button" data-reader-origin-key={`inbox-brief:${source.item_id}`} onClick={() => onOpenSource(source)}><BookOpen aria-hidden="true" />Open original</button>
                    {brief.status === "inbox" && source.disposition === "covered" ? <button type="button" disabled={!online || busySource === source.item_id} onClick={() => onKeep(source)}>{busySource === source.item_id ? "Keeping…" : "Keep in Inbox"}</button> : null}
                    {brief.status === "archive" && source.disposition === "archived" ? <button type="button" disabled={!online || busySource === source.item_id} onClick={() => onRestore(source)}><RotateCcw aria-hidden="true" />{busySource === source.item_id ? "Restoring…" : "Restore to Inbox"}</button> : null}
                  </div>
                </article>
              ))}
            </section>
          </article>
        ) : null}
      </div>
      {brief?.status === "inbox" ? <footer className="reader-bottom-actions brief-disposition"><button type="button" disabled={!online || archiveBusy} onClick={onArchive}><Archive aria-hidden="true" />{archiveBusy ? "Archiving…" : "Archive covered originals"}</button><span>Kept and changed sources stay in Inbox.</span></footer> : null}
    </section>
  );
}

function dispositionLabel(value: InboxBriefSource["disposition"]): string {
  if (value === "skipped_changed") return "Changed · not archived";
  if (value === "kept") return "Kept in Inbox";
  if (value === "archived") return "Archived by this brief";
  if (value === "restored") return "Restored to Inbox";
  return "Covered";
}
